import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Pencil, Trash2, Plus, Save, X, Coins, Clock, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import {
  useWeeklyTaskTemplatesWithSchedules,
  useAddWeeklyTaskTemplate,
  useUpdateWeeklyTaskTemplate,
  useDeleteWeeklyTaskTemplate,
  WeeklyTaskWithSchedules,
  WeeklyTaskSchedule,
} from '@/hooks/useWeeklyTasks';

interface WeeklyTasksManagementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const DAYS = [
  { value: 'Sunday', label: 'ראשון' },
  { value: 'Monday', label: 'שני' },
  { value: 'Tuesday', label: 'שלישי' },
  { value: 'Wednesday', label: 'רביעי' },
  { value: 'Thursday', label: 'חמישי' },
  { value: 'Friday', label: 'שישי' },
  { value: 'Saturday', label: 'שבת' },
];

const HOURS = Array.from({ length: 17 }, (_, i) => i + 7);

interface ScheduleEntry {
  day: string;
  hour: number | null;
  minute: number;
}

interface TaskFormData {
  title: string;
  hebrew_title: string;
  description: string;
  hebrew_description: string;
  coins: number;
  user_name: string;
  schedules: ScheduleEntry[];
}

const emptyForm: TaskFormData = {
  title: '',
  hebrew_title: '',
  description: '',
  hebrew_description: '',
  coins: 0,
  user_name: 'matan',
  schedules: [],
};

export function WeeklyTasksManagementDialog({
  open,
  onOpenChange,
}: WeeklyTasksManagementDialogProps) {
  const { data: templates = [], isLoading } = useWeeklyTaskTemplatesWithSchedules();
  const addMutation = useAddWeeklyTaskTemplate();
  const updateMutation = useUpdateWeeklyTaskTemplate();
  const deleteMutation = useDeleteWeeklyTaskTemplate();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [formData, setFormData] = useState<TaskFormData>(emptyForm);

  const startEdit = (template: WeeklyTaskWithSchedules) => {
    setEditingId(template.id);
    setIsAdding(false);
    setFormData({
      title: template.title,
      hebrew_title: template.hebrew_title || template.title || '',
      description: template.description || '',
      hebrew_description: template.hebrew_description || '',
      coins: template.coins,
      user_name: template.user_name,
      schedules: template.schedules.map(s => ({
        day: s.scheduled_day,
        hour: s.scheduled_hour,
        minute: s.scheduled_minute || 0,
      })),
    });
  };

  const startAdd = () => {
    setIsAdding(true);
    setEditingId(null);
    setFormData(emptyForm);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setIsAdding(false);
    setFormData(emptyForm);
  };

  const handleSave = async () => {
    if (!formData.hebrew_title.trim()) {
      toast.error('נא להזין שם משימה בעברית');
      return;
    }

    try {
      const schedulesForDb = formData.schedules.map(s => ({
        scheduled_day: s.day,
        scheduled_hour: s.hour,
        scheduled_minute: s.minute,
      }));

      if (isAdding) {
        await addMutation.mutateAsync({
          title: formData.title || formData.hebrew_title,
          hebrew_title: formData.hebrew_title,
          description: formData.description || null,
          hebrew_description: formData.hebrew_description || null,
          coins: formData.coins,
          user_name: formData.user_name,
          schedules: schedulesForDb,
        });
        toast.success('משימה נוספה בהצלחה');
      } else if (editingId) {
        await updateMutation.mutateAsync({
          id: editingId,
          updates: {
            title: formData.title || formData.hebrew_title,
            hebrew_title: formData.hebrew_title,
            description: formData.description || null,
            hebrew_description: formData.hebrew_description || null,
            coins: formData.coins,
            user_name: formData.user_name,
          },
          schedules: schedulesForDb,
        });
        toast.success('משימה עודכנה בהצלחה');
      }
      cancelEdit();
    } catch (error) {
      toast.error('שגיאה בשמירת המשימה');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('האם אתה בטוח שברצונך למחוק משימה זו?')) return;
    
    try {
      await deleteMutation.mutateAsync(id);
      toast.success('משימה נמחקה בהצלחה');
      if (editingId === id) {
        cancelEdit();
      }
    } catch (error) {
      toast.error('שגיאה במחיקת המשימה');
    }
  };

  const getDayLabel = (day: string) => {
    return DAYS.find(d => d.value === day)?.label || day;
  };

  const formatScheduleSummary = (schedules: WeeklyTaskSchedule[]) => {
    if (schedules.length === 0) return 'לבצע השבוע';
    
    const days = schedules.map(s => getDayLabel(s.scheduled_day));
    const uniqueDays = [...new Set(days)];
    
    if (uniqueDays.length <= 2) {
      return uniqueDays.join(', ');
    }
    return `${uniqueDays.length} ימים`;
  };

  const formatTime = (hour: number | null, minute: number | null) => {
    if (hour === null) return '';
    return `${hour.toString().padStart(2, '0')}:${(minute || 0).toString().padStart(2, '0')}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] bg-slate-800 border-slate-700 text-slate-100" dir="rtl">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-slate-100">
            ניהול משימות שבועיות
          </DialogTitle>
        </DialogHeader>

        <div className="flex justify-end mb-4">
          <Button
            onClick={startAdd}
            disabled={isAdding || !!editingId}
            className="bg-sky-600 hover:bg-sky-700 text-white"
          >
            <Plus className="h-4 w-4 ml-2" />
            הוסף משימה חדשה
          </Button>
        </div>

        <ScrollArea className="h-[60vh]">
          <div className="space-y-3 pl-4">
            {/* Add Form */}
            {isAdding && (
              <TaskForm
                formData={formData}
                setFormData={setFormData}
                onSave={handleSave}
                onCancel={cancelEdit}
                isLoading={addMutation.isPending}
              />
            )}

            {isLoading ? (
              <div className="text-center py-8 text-slate-400">טוען...</div>
            ) : templates.length === 0 && !isAdding ? (
              <div className="text-center py-8 text-slate-400">
                אין משימות שבועיות. לחץ על "הוסף משימה חדשה" כדי להתחיל.
              </div>
            ) : (
              templates.map((template) => (
                <div key={template.id}>
                  {editingId === template.id ? (
                    <TaskForm
                      formData={formData}
                      setFormData={setFormData}
                      onSave={handleSave}
                      onCancel={cancelEdit}
                      isLoading={updateMutation.isPending}
                    />
                  ) : (
                    <div className="flex items-center justify-between p-4 bg-slate-700 rounded-lg border border-slate-600">
                      <div className="flex-1">
                        <div className="font-medium text-slate-100">
                          {template.title}
                        </div>
                        <div className="text-sm text-slate-400 flex items-center gap-3 mt-1 flex-wrap">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatScheduleSummary(template.schedules)}
                          </span>
                          {template.schedules.length > 0 && template.schedules[0].scheduled_hour !== null && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {template.schedules.map(s => formatTime(s.scheduled_hour, s.scheduled_minute)).filter(Boolean).join(', ')}
                            </span>
                          )}
                          {template.coins > 0 && (
                            <span className="flex items-center gap-1">
                              <Coins className="h-3 w-3" />
                              {template.coins}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => startEdit(template)}
                          disabled={isAdding || !!editingId}
                          className="text-slate-300 hover:text-slate-100 hover:bg-slate-600"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(template.id)}
                          disabled={isAdding || !!editingId}
                          className="text-red-400 hover:text-red-300 hover:bg-slate-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

interface TaskFormProps {
  formData: TaskFormData;
  setFormData: (data: TaskFormData) => void;
  onSave: () => void;
  onCancel: () => void;
  isLoading: boolean;
}

function TaskForm({ formData, setFormData, onSave, onCancel, isLoading }: TaskFormProps) {
  const toggleDay = (dayValue: string) => {
    const existingIndex = formData.schedules.findIndex(s => s.day === dayValue);
    
    if (existingIndex >= 0) {
      // Remove this day
      setFormData({
        ...formData,
        schedules: formData.schedules.filter((_, i) => i !== existingIndex),
      });
    } else {
      // Add this day with default time
      setFormData({
        ...formData,
        schedules: [...formData.schedules, { day: dayValue, hour: null, minute: 0 }],
      });
    }
  };

  const updateScheduleTime = (dayValue: string, hour: number | null, minute: number) => {
    setFormData({
      ...formData,
      schedules: formData.schedules.map(s => 
        s.day === dayValue ? { ...s, hour, minute } : s
      ),
    });
  };

  const isDaySelected = (dayValue: string) => {
    return formData.schedules.some(s => s.day === dayValue);
  };

  const getScheduleForDay = (dayValue: string) => {
    return formData.schedules.find(s => s.day === dayValue);
  };

  return (
    <div className="p-4 bg-slate-700 rounded-lg border-2 border-sky-600 shadow-md space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="hebrew_title" className="text-slate-300">שם המשימה (עברית) *</Label>
          <Input
            id="hebrew_title"
            value={formData.hebrew_title}
            onChange={(e) => setFormData({ ...formData, hebrew_title: e.target.value })}
            placeholder="שם המשימה"
            className="mt-1 bg-slate-600 border-slate-500 text-slate-100 placeholder:text-slate-400"
          />
        </div>
        <div>
          <Label htmlFor="title" className="text-slate-300">שם המשימה (אנגלית)</Label>
          <Input
            id="title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="Task name"
            className="mt-1 bg-slate-600 border-slate-500 text-slate-100 placeholder:text-slate-400"
            dir="ltr"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="hebrew_description" className="text-slate-300">תיאור (עברית)</Label>
          <Input
            id="hebrew_description"
            value={formData.hebrew_description}
            onChange={(e) => setFormData({ ...formData, hebrew_description: e.target.value })}
            placeholder="תיאור המשימה"
            className="mt-1 bg-slate-600 border-slate-500 text-slate-100 placeholder:text-slate-400"
          />
        </div>
        <div>
          <Label htmlFor="description" className="text-slate-300">תיאור (אנגלית)</Label>
          <Input
            id="description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Task description"
            className="mt-1 bg-slate-600 border-slate-500 text-slate-100 placeholder:text-slate-400"
            dir="ltr"
          />
        </div>
      </div>

      {/* Days Selection with Checkboxes */}
      <div>
        <Label className="mb-2 block text-slate-300">ימים בשבוע</Label>
        <div className="grid grid-cols-7 gap-2">
          {DAYS.map((day) => (
            <div 
              key={day.value}
              className={`
                p-2 rounded-lg border text-center cursor-pointer transition-all
                ${isDaySelected(day.value) 
                  ? 'border-sky-500 bg-sky-600/30' 
                  : 'border-slate-600 hover:border-sky-500/50 bg-slate-600'
                }
              `}
              onClick={() => toggleDay(day.value)}
            >
              <Checkbox
                checked={isDaySelected(day.value)}
                onCheckedChange={() => toggleDay(day.value)}
                className="mb-1 border-slate-400 data-[state=checked]:bg-sky-600 data-[state=checked]:border-sky-600"
              />
              <div className="text-xs font-medium text-slate-200">{day.label}</div>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-400 mt-1">
          אם לא נבחר יום - המשימה תופיע ב"לבצע השבוע"
        </p>
      </div>

      {/* Time Selection per Day */}
      {formData.schedules.length > 0 && (
        <div>
          <Label className="mb-2 block text-slate-300">שעות לכל יום</Label>
          <div className="space-y-2">
            {formData.schedules.map((schedule) => (
              <div key={schedule.day} className="flex items-center gap-3 bg-slate-600/50 p-2 rounded">
                <span className="w-16 text-sm font-medium text-slate-200">
                  {DAYS.find(d => d.value === schedule.day)?.label}
                </span>
                <Select
                  value={schedule.hour?.toString() || 'none'}
                  onValueChange={(value) =>
                    updateScheduleTime(
                      schedule.day,
                      value === 'none' ? null : parseInt(value),
                      schedule.minute
                    )
                  }
                >
                  <SelectTrigger className="w-28 bg-slate-600 border-slate-500 text-slate-100">
                    <SelectValue placeholder="שעה" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    <SelectItem value="none" className="text-slate-200 focus:bg-slate-600 focus:text-slate-100">ללא שעה</SelectItem>
                    {HOURS.map((hour) => (
                      <SelectItem key={hour} value={hour.toString()} className="text-slate-200 focus:bg-slate-600 focus:text-slate-100">
                        {hour.toString().padStart(2, '0')}:00
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Coins */}
      <div className="w-32">
        <Label htmlFor="coins" className="text-slate-300">מטבעות</Label>
        <Input
          id="coins"
          type="number"
          min="0"
          value={formData.coins}
          onChange={(e) => setFormData({ ...formData, coins: parseInt(e.target.value) || 0 })}
          className="mt-1 bg-slate-600 border-slate-500 text-slate-100"
        />
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading} className="bg-slate-600 border-slate-500 text-slate-100 hover:bg-slate-500">
          <X className="h-4 w-4 ml-2" />
          ביטול
        </Button>
        <Button
          type="button"
          onClick={onSave}
          disabled={isLoading}
          className="bg-sky-600 hover:bg-sky-700 text-white"
        >
          <Save className="h-4 w-4 ml-2" />
          שמור
        </Button>
      </div>
    </div>
  );
}
